import React, { type ReactElement, useMemo, useState } from 'react';

import classnames from 'classnames';
import { useInView } from 'react-intersection-observer';

import { type SizeType, useSize } from '../../features/config';
import { type ThemedColors } from '../../types/themed-colors';

import { getAvatarColorByInitials, makeInitials } from './Avatar.utils';
import avatarPlaceholder from './placeholder.svg';

import styles from './Avatar.module.scss';

const NAME_INITIALS_STYLES = [
    styles['initialsContainer--1'],
    styles['initialsContainer--2'],
    styles['initialsContainer--3'],
    styles['initialsContainer--4'],
    styles['initialsContainer--5'],
    styles['initialsContainer--6'],
    styles['initialsContainer--7'],
    styles['initialsContainer--8']
];

export type AvatarProps = {
    className?: string;
    /**
     * The size of the avatar.
     */
    size?: SizeType;
    /**
     * The custom size of the avatar. If provided it will override the `size` prop.
     */
    customSize?: string | number;
    /**
     * The shape of the avatar.
     */
    shape?: 'circle' | 'square' | 'rounded';
    /**
     * The address or base64 of the avatar image.
     */
    image?: string;
    /**
     * A text to describe the image. If used with `withInitials` it will be used as the initials.
     */
    alt?: string;
    /**
     * The text color of the initials. If not provided it will be generated based on the initials.
     */
    initialColor?: string;
    /**
     * The background color of the initials. If not provided it will be generated based on the initials.
     */
    initialBackgroundColor?: string;
    /**
     * The border color of the avatar. Not rendered if `noBorder` is `true`.
     */
    borderColor?: ThemedColors;
    /**
     * Whether the avatar should show a pointer cursor on hover.
     */
    withPointer?: boolean;
    /**
     * The initials to be shown if no image is provided. If not provided it will be generated based on the `alt` prop.
     */
    initials?: string;
    /**
     * Whether the initials should be shown if no image is provided.
     */
    withInitials?:
        | boolean
        | {
              size: number | string;
          };
    /**
     * Whether the avatar should not have a border.
     */
    noBorder?: boolean;
    /**
     * Whether the avatar should not have a placeholder. A gray circle will be shown instead.
     */
    noPlaceholder?: boolean;
    /**
     * The border size of the avatar. If provided it will override the one from the styles.
     * If `noBorder` is `true` this prop will be ignored.
     */
    borderWidth?: SizeType;
    style?: React.CSSProperties;
};

export const Avatar = ({
    className,
    size: sizeProp,
    customSize,
    shape = 'circle',
    image,
    alt = '',
    initialColor,
    initialBackgroundColor,
    borderColor,
    withPointer,
    initials,
    withInitials = true,
    noBorder,
    noPlaceholder,
    style,
    borderWidth = 'small',
    ...rest
}: AvatarProps): ReactElement => {
    const [isImageLoaded, setIsImageLoaded] = useState(false);

    const { inView, ref: inViewRef } = useInView({ triggerOnce: true, delay: 500 });

    const initialsSize = typeof withInitials === 'object' ? withInitials.size : null;
    const avatarInitials = initials || makeInitials(alt);
    const shouldRenderInitials = withInitials && Boolean(avatarInitials) && !image;
    const size = useSize(sizeProp, 'medium', 'Avatar');
    const customSizeStyle = customSize ? { width: customSize, height: customSize } : {};
    const initialsStyle = initialsSize ? { fontSize: initialsSize } : {};

    const wrapperClassName = classnames(
        styles.wrapper,
        {
            [styles.withPointer]: withPointer,
            [styles.initials]: shouldRenderInitials,
            [styles.noBorder]: noBorder,
            [styles.noImageProvided]: !image,
            [styles.noPlaceholder]: noPlaceholder,
            'chromatic-ignore': true
        },
        styles[size],
        className
    );

    const avatarClassName = classnames(
        styles.avatarElement,
        styles[shape],
        borderColor ? styles[borderColor] : '',
        styles[`border-${borderWidth}`]
    );

    const handleOnLoad = () => {
        setIsImageLoaded(true);
    };

    const handleOnError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const target = e.target as HTMLImageElement;
        target.src = avatarPlaceholder;
    };

    const getElement = () => {
        const imageClassNames = classnames(styles.image, {
            [styles.noBorder]: noBorder
        });

        if (shouldRenderInitials) {
            return (
                <div
                    className={classnames(
                        styles.initialsContainer,
                        getAvatarColorByInitials(NAME_INITIALS_STYLES, avatarInitials)
                    )}
                    style={{ color: initialColor, backgroundColor: initialBackgroundColor }}
                    role="img"
                    aria-label={alt}
                >
                    {avatarInitials}
                </div>
            );
        }
        return (
            <img
                className={imageClassNames}
                alt={alt}
                src={image ?? avatarPlaceholder}
                loading="lazy"
                onLoad={handleOnLoad}
                onError={handleOnError}
                style={{
                    backgroundImage: isImageLoaded ? 'none' : `url("${avatarPlaceholder}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            />
        );
    };

    const avatarStyles = useMemo(() => {
        return {
            borderColor: shouldRenderInitials ? '' : borderColor,
            ...style
        };
    }, [shouldRenderInitials, alt, borderColor]);

    return (
        <div
            className={wrapperClassName}
            style={{ ...customSizeStyle, ...initialsStyle }}
            ref={inViewRef}
        >
            <div className={avatarClassName} style={avatarStyles} {...rest}>
                {inView && getElement()}
            </div>
        </div>
    );
};
